/** @flow */
import { all, put, takeEvery, call } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import {
  GetServiceAbsenceManagementSettings,
  UpdateServiceAbsenceManagementSettings,
  GetClientAITeamSettings,
  UpdateClientAITeamSettings,
} from "@modules/client/constants/actions";
import actionCreators from "@modules/client/actionCreator";
import settingsCreators from "@modules/settings/actionCreator";
import type {
  ClientServiceAbsenceManagement,
  AITeamSettings,
} from "@modules/client/reducer/service/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import config from "@config";
import { getSchema } from "@utils/schema";

type ClientRequest = $Exact<{
  clientId: string,
}>;

type ServiceUpdateRequest = $Exact<{
  ...ClientRequest,
  formData: ClientServiceAbsenceManagement,
}>;

type AITeamSettingsUpdateRequest = $Exact<{
  ...ClientRequest,
  data: AITeamSettings,
}>;

const aiTeamSettingsEndpoint = (clientId: string) =>
  `${config.endpointBaseUrl}api/v1/service-admin/client/${clientId}/service-config/platform/ai-team-settings`;

export function* onGetServiceAbsenceManagementSettings({
  payload,
}: Action<ClientRequest>): Generator<*, void, ClientServiceAbsenceManagement> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const [response] = yield all([
      call(
        http.get,
        `${config.endpointBaseUrl}api/v1/client-admin/client/${payload.clientId}/service/absence-management`,
      ),
    ]);

    const schema = yield getSchema("AbsenceManagementClientServiceDTO");

    yield all([
      put(actionCreators.GetClientServiceSuccess(response.data)),
      put(actionCreators.SetClientServiceSchema(schema)),
    ]);
  } catch (e) {
    yield put(actionCreators.GetClientServiceError(e.response));
  }
}

export function* onUpdateServiceAbsenceManagementSettings({
  payload,
}: Action<ServiceUpdateRequest>): Generator<*, void, { data: ClientServiceAbsenceManagement }> {
  try {
    if (!payload || !payload.clientId || !payload.formData) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `api/v1/client-admin/client/${payload.clientId}/service/absence-management`,
      {
        ...payload.formData,
      },
    );

    yield all([
      put(sendSuccessMessage("SERVICE_SCOPE_WAS_SUCCESSFULLY_UPDATED")),
      put(actionCreators.GetClientServiceSuccess(response.data)),
      put(settingsCreators.GetSettings(payload)),
      put(actionCreators.ToggleSelfServiceModal(false)),
    ]);
  } catch (e) {
    yield put(actionCreators.GetClientServiceError(e.response));
  }
}

export function* onGetClientAITeamSettings({
  payload,
}: Action<ClientRequest>): Generator<*, void, { data: AITeamSettings }> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(http.get, aiTeamSettingsEndpoint(payload.clientId));

    yield put(actionCreators.GetClientAITeamSettingsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetClientAITeamSettingsError(e.response));
  }
}

export function* onUpdateClientAITeamSettings({
  payload,
}: Action<AITeamSettingsUpdateRequest>): Generator<*, void, { data: AITeamSettings }> {
  try {
    if (!payload || !payload.clientId || !payload.data) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(http.put, aiTeamSettingsEndpoint(payload.clientId), payload.data);

    yield all([
      put(sendSuccessMessage("AI_TEAM_SETTINGS_WERE_SUCCESSFULLY_UPDATED")),
      put(actionCreators.UpdateClientAITeamSettingsSuccess(response.data || payload.data)),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateClientAITeamSettingsError(e.response));
  }
}

// ROOT
export function* ClientServiceSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetServiceAbsenceManagementSettings, onGetServiceAbsenceManagementSettings),
    takeEvery(UpdateServiceAbsenceManagementSettings, onUpdateServiceAbsenceManagementSettings),
    takeEvery(GetClientAITeamSettings, onGetClientAITeamSettings),
    takeEvery(UpdateClientAITeamSettings, onUpdateClientAITeamSettings),
  ]);
}

export default ClientServiceSagas;
